import { z } from 'zod';
import { ApiSuccessResponseSchema } from './api';

/**
 * MFA-Related Schemas
 * 
 * Epic 2 - Story 2.3: Runtime Type Safety
 * 
 * These schemas validate MFA operations including push, TOTP, and verification.
 */

/**
 * MFA Factor Type
 */
export const MfaFactorTypeSchema = z.enum([
  'push',
  'totp',
  'token:software:totp',
  'token:hardware',
  'hardware_token',
  'sms',
  'call',
  'email',
  'webauthn',
  'external_idp',
  'signed_nonce',
]);

export type MfaFactorType = z.infer<typeof MfaFactorTypeSchema>;

/**
 * MFA Provider
 */
export const MfaProviderSchema = z.enum([
  'OKTA',
  'GOOGLE',
  'FIDO',
  'DUO',
  'SYMANTEC',
]);

export type MfaProvider = z.infer<typeof MfaProviderSchema>;

/**
 * MFA Factor Status
 */
export const MfaFactorStatusSchema = z.enum([
  'ACTIVE',
  'INACTIVE',
  'PENDING_ACTIVATION',
  'ENROLLED',
  'NOT_SETUP',
]);

export type MfaFactorStatus = z.infer<typeof MfaFactorStatusSchema>;

/**
 * MFA Factor
 * Note: Using string for flexible fields to allow unknown types
 */
export const MfaFactorSchema = z.object({
  id: z.string(),
  factorType: z.string(), // Accept any string (known or unknown types)
  provider: z.string(),
  vendorName: z.string().optional(),
  status: z.string(),
  created: z.string().optional(),
  lastUpdated: z.string().optional(),
  profile: z.record(z.string(), z.unknown()).optional(),
  _links: z.record(z.string(), z.unknown()).optional(),
  _embedded: z.record(z.string(), z.unknown()).optional(),
});

export type MfaFactor = z.infer<typeof MfaFactorSchema>;

/**
 * Send Passcode Response
 */
export const SendPasscodeResponseSchema = ApiSuccessResponseSchema(
  z.object({
    session_id: z.string().nullable().optional(),
    status: z.string().nullable().optional(),
    next: z.string().nullable().optional(),
  })
);

export type SendPasscodeResponse = z.infer<typeof SendPasscodeResponseSchema>;

/**
 * Send MFA Challenge Response
 */
export const SendMfaChallengeResponseSchema = ApiSuccessResponseSchema(
  z.object({
    session_id: z.string().nullable().optional(),
    challenge_id: z.string().nullable().optional(),
    status: z.string().nullable().optional(),
    next: z.string().nullable().optional(),
    expiresAt: z.string().nullable().optional(),
    _links: z.record(z.string(), z.any()).optional(),
  })
);

export type SendMfaChallengeResponse = z.infer<typeof SendMfaChallengeResponseSchema>;

/**
 * Check MFA Status Response
 */
export const CheckMfaStatusResponseSchema = ApiSuccessResponseSchema(
  z.object({
    session_id: z.string().nullable().optional(),
    status: z.string().nullable().optional(),
    next: z.string().nullable().optional(),
    factorResult: z.string().nullable().optional(),
  })
);

export type CheckMfaStatusResponse = z.infer<typeof CheckMfaStatusResponseSchema>;

/**
 * MFA Verification Result
 * Not from API - internal use
 */
export const MfaVerificationResultSchema = z.object({
  status: z.enum(['accepted', 'waiting', 'timeout', 'error']),
  sessionId: z.string().optional(),
  errorMsg: z.string().optional(),
});

export type MfaVerificationResult = z.infer<typeof MfaVerificationResultSchema>;

/**
 * MFA Active Data
 * Frontend-internal shape composed from multiple API responses.
 * Plain TS interface (not a Zod schema) — never received raw from the wire.
 */
export interface MfaActiveData {
  factorType: string;
  factorId: string;
  factorName: string;
  userId: string;
  userEmail: string;
  userFirstName: string;
}

